import { useMemo } from "react";
import { Alert, Skeleton } from "antd";

import { Client } from "../../../bitmaker/schemas/Billing/Client";

interface ClientDetailOverviewProps {
  client: Client | null;
  loading: boolean;
  error: boolean;
}

function OverviewField({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  const display = value?.trim() || "—";

  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
        {label}
      </div>
      <div className="text-sm text-gray-900">{display}</div>
    </div>
  );
}

export default function ClientDetailOverview({
  client,
  loading,
  error,
}: ClientDetailOverviewProps) {
  const addressBlock = useMemo(() => {
    if (!client) return "—";

    const lines = [
      client.addressLine,
      [client.townCity, client.stateProvince].filter(Boolean).join(", "),
      [client.postalCode, client.country].filter(Boolean).join(" "),
    ]
      .map((s) => s?.trim())
      .filter(Boolean);

    return lines.length ? lines.join("\n") : "—";
  }, [client]);

  const isInitialLoading = loading && !client;

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-xl min-h-full">
        {error && !client ? (
          <div className="p-6">
            <Alert
              type="error"
              showIcon
              message="Failed to load client information"
              description="Please try again later."
            />
          </div>
        ) : isInitialLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[300px] p-6">
            <Skeleton active paragraph={{ rows: 6 }} className="mt-4" />
          </div>
        ) : client ? (
          <div className="p-8">
            <h3 className="text-sm font-bold uppercase text-gray-500 mb-6 tracking-wide">
              Overview
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <OverviewField label="Email" value={client.emailAddress} />
              <OverviewField label="Phone" value={client.phoneNumber} />

              <div className="sm:col-span-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Address
                </div>
                <div className="text-sm text-gray-900 whitespace-pre-line">
                  {addressBlock}
                </div>
              </div>

              <OverviewField label="Tax name" value={client.taxName} />
              <OverviewField label="Tax number" value={client.taxNumber} />

              <OverviewField label="Default Currency" value={client.currency} />
              <OverviewField label="Default Language" value={client.language} />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
